import React from "react";
import { Lead, Section, P, CTACard } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "盲目信任 AI";
export const excerpt =
  "我们已经习惯了飞快地合并完全由 AI 写出的成果。但 Agent 停下的那一刻，并不等于成果就能安全上线。一旦合并，它就归你负责了。";
export const heroAlt = "莫比乌斯风格的风景：黄昏时分，一棵孤树立在山谷之上的绿色山坡上，粉色的云与一弯新月";
export const readingTime = "4 分钟阅读";

const BLUE = "#5ba8f5";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "Anthropic，《Common workflows》，Claude Code 文档。用于说明 Claude Code 在代码库探索、测试、创建与审查 PR、定时任务、worktree、plan 模式和 subagent 等方面的工作流。",
    href: "https://code.claude.com/docs/en/common-workflows",
    label: "code.claude.com/docs/en/common-workflows",
  },
  {
    text: "N. Perry、M. Srivastava、D. Kumar、D. Boneh，《Do Users Write More Insecure Code with AI Assistants?》（斯坦福）。用于说明这一发现：用了 AI 助手的参与者写出的代码明显更不安全，却更倾向于相信自己的代码是安全的。",
    href: "https://arxiv.org/abs/2211.03622",
    label: "arxiv.org/abs/2211.03622",
  },
  {
    text: "Sonar，《State of Code Developer Survey report》（2026）。用于说明：61% 的开发者认为 AI 常常写出看起来正确、实则不可靠的代码，96% 不完全信任 AI 的输出，而只有 48% 会每次都去验证它——也就是那道验证瓶颈。",
    href: "https://www.sonarsource.com/blog/state-of-code-developer-survey-report-the-current-reality-of-ai-coding",
    label: "sonarsource.com/…/state-of-code-developer-survey-report",
  },
  {
    text: "Anthropic，《Code Review》，Claude Code 文档。用于说明多个 Agent 并行审查一个 pull request，用一个验证步骤过滤掉误报，再把发现去重并按严重程度排序。",
    href: "https://code.claude.com/docs/en/code-review",
    label: "code.claude.com/docs/en/code-review",
  },
  {
    text: "OpenSSF，《Security-Focused Guide for AI Code Assistant Instructions》。用于说明这条原则：开发者始终掌控并对 AI 生成的代码负责，应当审慎评估并修改它，而不是盲目接受。",
    href: "https://best.openssf.org/Security-Focused-Guide-for-AI-Code-Assistant-Instructions.html",
    label: "best.openssf.org/…/AI-Code-Assistant-Instructions",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        我们相当快地就适应了用 AI 写代码。但让我意外的是，我们竟这么快就习惯了直接接受并合并完全由 AI 写出的功能与重构。（至少我是这么觉得的。）
      </Lead>

      <P>
        Claude 的工作流，如今已经不再只是生成一个函数，而是用 Claude 去探索一个陌生的代码库、追一条 auth 流程、加测试、发起 pull request、恢复旧会话、在 worktree 里并行干活、动手改之前先做规划、把调研交给 subagent，甚至跑像审查开放 PR 这样的定时任务。<Fn n={1} /> 这已经更像一个在仓库里来回穿梭、留下一串看起来像成果的痕迹的初级开发者了。
      </P>
      <P>
        而这正是让人不安的地方。当一个 Agent 干完活，它的输出通常都带着完成品的模样：一份计划、改动过的文件、跑过的命令、一段总结，有时还有 PR 描述、测试输出，有时甚至附上一条提示，说明有哪些风险要留意。它让这份成果拥有了完成品的外观与手感。我常常发现自己在盲目地信任这种输出。纯粹是出于懒。哪怕我清楚 LLM 的输出仍然只是数学上的概率，而不是真相。<Fn n={1} /> 这种输出可以很有用，却仍然不是最终版。
      </P>
      <P>
        这些成果不是假的，也不是胡言乱语。大多数时候它是合理而连贯的，它能编译，通常也能通过测试。Agent 确实做了点什么，也许还做对了。但它仍然可能引入 bug、错误，乃至更糟的问题。<Fn n={2} />
      </P>

      <Section title="那 review 呢？">
        <P>
          在自动补全时代，也就是 GitHub Copilot、Cursor、Windsurf 这类早期工具里，review 仍然是流程的一部分，而且默认是小份的。一条建议、几行代码，接受或拒绝。哪怕它错了，你也正站在代码旁边，所以错误只停留在局部。而今天这些同样的工具，一次就交付整个功能、大段代码，往往单次运行就是好几千行，而 review 却从没相应地缩小规模。
        </P>
        <P>
          在 Agentic 工作流，或者像 Claude Code 这样的多 Agent 动态工作流里干活，又是另一回事。Agent 会搜索、编辑、跑一次测试、读报错、再改一遍，也许装个包，也许开一条 branch，然后用一种听起来很像盖棺定论的语气把整份活儿总结出来（有了 Agent 集群之后更糟）。于是问题就变了。你不再只是问代码对不对。你要问的是，这条解题路径走得对不对。它读的是不是该读的文件？它有没有误读架构？它是不是因为测试太弱就走了捷径？它是不是改动了公开行为，却只验证了顺利路径？
        </P>
        <P>
          这要 review 起来难得多，因为你需要审的东西有一半是看不见的。diff 就在那儿；可它背后的推理大多不在。plan 模式和 subagent 能帮上忙：Agent 在动文件之前先提出一份计划，还能在一个单独的上下文里调查完再回来汇报。<Fn n={1} /> Agent 越有本事，人就越需要一份关于「路径」的记录，而不只是那份补丁。
        </P>
      </Section>

      <Section title="「完成」不再意味着完成">
        <P>
          我们还是照旧说「完成」，仿佛它只指一件事。但在 Agentic 编程里，「完成」并不是一个状态，而是一整叠彼此独立的断言。Agent 停下了。代码能编译。测试通过。风险可接受。开发者真的理解了这次改动。一个 Agent 可以命中其中几条，却悄悄漏掉其余的。它可以解决任务，却仍然留下一个窟窿。它可以跑测试，却仍然跳过了那个真正要紧的行为。在我看来，被混为一谈的正是这一点：「Agent 停下了」和「这可以安全上线」感觉像是同一个时刻，但它们并不是——而这也划出了 vibe coding 与真正编程之间的界线。
        </P>
        <P>
          所以在我看来，「Agent 将取代开发者」并不算是错的担忧。它会让开发岗位减少，事实上已经在减少了，尤其是初级岗位。但这个说法的着眼点仍然偏了。真正的问题更平淡，也潜伏在这一切之下：Agent 让我们产出代码的速度，涨得比我们真正能验证代码的速度更快。<Fn n={3} /> 这道差距有个名字。它叫 review 债，而且正在增长。
        </P>
      </Section>

      <Section title="它终究归你负责">
        <P>
          Agent 可以写补丁、解释它、跑测试，甚至 review 它。但从合并的那一刻起，结果就归你负责。<Fn n={5} /> 你不能对客户说，这个 bug 因为是 Agent 写的就没那么真实。你也不能对下一个工程师说，没人看得懂这段代码，因为它是 Claude 在凌晨 1:37 写的，而当时 PR 看起来没问题。
        </P>
        <P>
          反正「负责」从来都不是指亲手敲下每一个字符。我们本就依赖库、框架、生成的客户端、脚手架、CI。这份工作真正的内核一直是判断力。Agent 只是让这一点更难被忽视了。
        </P>
        <P>
          越来越多时候，review 的是另一个 Agent。Agent 写代码，Agent review 代码。<Fn n={4} /> 这可以奏效，但也可能只是把两份自信满满的总结叠在一起，然后管它叫共识。我和我的联合创始人（也是我兄弟）在做 Cate、以及在此之前做别的项目时，自己就一头撞进了这一切。我们合并得太快，因为 diff 看起来没问题，而我们想快点推进。尤其是在 Cate 最近这几个版本里，应用塞进了更多功能、也更打磨了，于是浮出了大量 bug、重复的架构系统和样板代码。我们靠一段漫长、艰难、至今仍在进行的工作把它清理干净。但我们从中学到并带走的是：我们想在不久的将来，把 Cate 里的代码 review 当成核心焦点之一。而且我们相信，凭借开放画布这个概念，我们拥有最好的基础和足够的「空间」，把这件事做得尽可能可视化、尽可能高效。
        </P>
        <P>
          让 Agent 的工作变得可检视：把任务亮出来，把计划亮出来，把它读过和改过的文件亮出来，把命令亮出来，把测试输出亮出来，把批准记录亮出来，也把那些缺口亮出来。把它作为画布上一份清晰的可视化概览呈现出来，这样在你 review 时，整条工作链始终可见，而不是埋在一段聊天记录或一段自信的文字里。
        </P>
        <P>然后，让开发者像对它负责那样去 review 它。</P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          因为，它确实归你负责。
        </p>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="参考文献">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
